import type { MouseEvent, ReactNode } from 'react'
import { AnimatePresence, motion, type Transition, type Variants } from 'framer-motion'
import { twMerge } from 'tailwind-merge'

const scrimVariants: Variants = {
  hidden: { opacity: 0, y: 10, filter: 'blur(4px)' },
  visible: { opacity: 1, y: 0, filter: 'blur(0px)' },
}

const scrimTransition: Transition = {
  type: 'spring',
  bounce: 0,
  duration: 0.4,
}

interface ViewConfirmScrimProps {
  isOpen: boolean
  /** Scrim-click cancel; clicks inside the card never reach it. */
  onCancel: (e: MouseEvent<HTMLDivElement>) => void
  /** Names the card and makes it a `role="alertdialog"`. */
  ariaLabel?: string
  /** Extends the card (spacing/rounding/width); the surface itself is fixed. */
  className?: string
  children: ReactNode
}

/**
 * In-view confirmation: a scrim over the view with a centered card, mounted at
 * the view root where a modal would go if the panel could host one — portaled
 * dialogs get clipped by the panel's transformed root. The one choreography
 * every confirmation here shares (DeleteConfirmation, UnsavedChangesDialog);
 * the card's contents and buttons are the caller's.
 */
export function ViewConfirmScrim({ isOpen, onCancel, ariaLabel, className, children }: ViewConfirmScrimProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          variants={scrimVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          transition={scrimTransition}
          onClick={onCancel}
          className="bg-background/70 absolute inset-0 z-10 grid place-items-center"
        >
          <div
            role={ariaLabel ? 'alertdialog' : undefined}
            aria-label={ariaLabel}
            className={twMerge('bg-background-subtle text-sm shadow-lg', className)}
            onClick={(e) => e.stopPropagation()}
          >
            {children}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
